import React from 'react';
import { motion } from 'framer-motion';
import Connections from './Connections';
import CountUp from './motion/CountUp';
import { fadeUp, Parallax, Reveal, StaggerContainer } from './motion/Reveal';

interface SkillCategory {
  title: string;
  // Full literal class names so Tailwind's scanner picks them up
  accent: string;
  items: { skill: string; description: string }[];
}

const skillCategories: SkillCategory[] = [
  {
    title: 'Programming Languages',
    accent: 'border-l-primary',
    items: [
      { skill: 'Golang', description: 'Primary language, backend development, automation' },
      { skill: 'Ruby', description: 'Web applications and system design' },
      { skill: 'Python', description: 'Data processing, scripting, and automation' },
      { skill: 'C#', description: 'System automation and tooling' },
      { skill: 'Node.js', description: 'Backend JavaScript development' },
      { skill: 'Kotlin', description: 'Backend services with Spring Boot' },
      { skill: 'Java', description: 'Enterprise application development' },
      { skill: 'SQL', description: 'Database querying and optimization' },
    ],
  },
  {
    title: 'Backend & Systems',
    accent: 'border-l-indigo-500',
    items: [
      { skill: 'Microservices', description: 'Developing scalable, distributed systems' },
      { skill: 'Distributed Systems', description: 'High-availability architecture at scale' },
      { skill: 'API Design', description: 'Creating and managing RESTful and event-driven APIs' },
    ],
  },
  {
    title: 'Cloud & Infra',
    accent: 'border-l-green-500',
    items: [
      { skill: 'GCP', description: 'Cloud operations, BigQuery, and data management' },
      { skill: 'Kubernetes', description: 'Container orchestration and service deployment' },
      { skill: 'Docker', description: 'Containerization of services' },
      { skill: 'Terraform', description: 'Infrastructure as code, deployment automation' },
      { skill: 'CI/CD', description: 'Continuous integration and delivery pipelines' },
    ],
  },
  {
    title: 'Data Engineering',
    accent: 'border-l-purple-500',
    items: [
      { skill: 'BigQuery', description: 'Managing and processing large datasets for analytics' },
      { skill: 'Apache Kafka', description: 'Stream processing and real-time data pipelines' },
      { skill: 'DBT', description: 'Data transformation and modeling' },
      { skill: 'Apache Airflow', description: 'Workflow orchestration for data pipelines' },
    ],
  },
  {
    title: 'Databases',
    accent: 'border-l-teal-500',
    items: [
      { skill: 'PostgreSQL', description: 'Relational database management and optimization' },
      { skill: 'MySQL', description: 'Relational database for web applications' },
      { skill: 'MongoDB', description: 'NoSQL document database' },
      { skill: 'Redis', description: 'In-memory data structure store for caching' },
      { skill: 'Elasticsearch', description: 'Search and analytics engine for large datasets' },
    ],
  },
  {
    title: 'Leadership',
    accent: 'border-l-orange-400',
    items: [
      { skill: 'Team Leadership', description: 'Leading cross-functional engineering teams of up to 10' },
      { skill: 'Mentoring', description: 'Developing junior engineers to mid and senior level' },
      { skill: 'System Design', description: 'Architecting scalable, maintainable systems' },
      { skill: 'Delivery Optimisation', description: 'Agile/Scrum, sprint management, and process improvement' },
    ],
  },
  {
    title: 'Education',
    accent: 'border-l-orange-400',
    items: [
      {
        skill: 'S. TI (BSc) Computer Science',
        description: 'Bina Nusantara, Indonesia — 2011–2015',
      },
    ],
  },
];

const highlights = [
  { value: 10, suffix: '+', label: 'Years Experience' },
  { value: 6, suffix: '+', label: 'Years Leading Teams' },
];

const About: React.FC = () => (
  <section className="bg-grey-50 py-16 md:py-20" id="about">
    <div className="container mx-auto flex flex-col items-start lg:items-center">
      {/* Who am I Section */}
      <div className="w-full px-4 text-center lg:w-full lg:text-left mb-10">
        <Reveal>
          <Parallax>
            <h2 className="font-header text-4xl font-semibold uppercase sm:text-5xl lg:text-6xl bg-gradient-to-r from-primary via-blue-400 to-primary bg-[length:200%_auto] bg-clip-text text-transparent motion-safe:animate-shimmer">
              Who am I?
            </h2>
          </Parallax>
        </Reveal>
        <Reveal delay={0.1}>
          <h4 className="pt-6 font-header text-xl font-medium text-black sm:text-2xl lg:text-3xl">
            I'm Michael Ryan, a Staff / Senior Software Engineer
          </h4>
          <p className="pt-6 font-body leading-relaxed text-grey-20">
            Staff Engineer with 10+ years of experience building scalable backend systems and leading engineering teams across fintech, e-commerce, and government sectors. I lead and mentor teams while staying hands-on — writing code, reviewing architecture, and staying close to the work. Experienced in Golang, distributed systems, and data pipelines, with a track record of balancing speed and quality to deliver reliable production systems at scale.
          </p>
        </Reveal>

        {/* Key Highlights */}
        <StaggerContainer className="grid grid-cols-2 gap-4 mt-6" stagger={0.15}>
          {highlights.map((item, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className="bg-white border border-grey-70 rounded-lg p-4 text-center hover:shadow-lg transition-shadow"
            >
              <CountUp
                to={item.value}
                suffix={item.suffix}
                className="text-3xl font-bold text-primary"
              />
              <div className="text-sm text-grey-20 uppercase mt-1">{item.label}</div>
            </motion.div>
          ))}
        </StaggerContainer>

        {/* Connections section */}
        <Reveal delay={0.2}>
          <div className="flex flex-col justify-center pt-6 sm:flex-row lg:justify-start">
            <div className="flex items-center justify-center sm:justify-start">
              <p className="font-body text-lg font-semibold uppercase text-grey-20">
                Connect with me
              </p>
              <div className="hidden sm:block">
                <i className="bx bx-chevron-right text-2xl text-primary"></i>
              </div>
            </div>
            <div className="flex items-center justify-center pt-5 pl-2 sm:justify-start sm:pt-0 text-primary">
              <Connections />
            </div>
          </div>
        </Reveal>
      </div>

      {/* Skills Section */}
      <Reveal>
        <h3 className="pt-6 text-center font-header text-xl font-medium text-primary sm:text-2xl lg:text-3xl">
          Related Skills
        </h3>
      </Reveal>
      <div className="w-full px-4 pl-0 pt-10 lg:w-full lg:pl-12 lg:pt-0">
        {skillCategories.map((category) => (
          <div key={category.title}>
            <Reveal>
              <h3 className="font-body font-semibold text-black uppercase mt-8 mb-4">
                {category.title}
              </h3>
            </Reveal>
            <StaggerContainer className="grid grid-cols-2 gap-4">
              {category.items.map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileHover={{ y: -6, scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className={`pt-4 pb-4 pl-4 pr-4 rounded-lg bg-white border border-grey-70 border-l-4 hover:shadow-lg transition-shadow ${category.accent}`}
                >
                  <h4 className="font-body font-semibold uppercase text-primary">{item.skill}</h4>
                  <div className="text-grey-20">{item.description}</div>
                </motion.div>
              ))}
            </StaggerContainer>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default About;
